/**
 * GlobalState.archive 切片与 Worker 数据源接线（任务 14.36）。
 *
 * 归档是**纯本地标记、不跨端同步**（旧版同口径，legacy §5.2）：事实源在 Worker
 * 账号库 kv（local-state.ts，`archive/<ConversationKey>`），本切片是主线程镜像，
 * 供会话列表分流消费（archiveModel.partitionArchived）。
 *
 * 写路径与 drafts 同构——只有主线程会改（用户在行菜单里点），所以没有推送通道：
 * setArchived 先乐观改切片（行即时从主列表消失），再 fire-and-forget 调
 * 'chat/archive/set' 落 kv。登录时整批快照一次（纯 IndexedDB 零 HTTP）。
 *
 * 边界（drafts 同口径）：
 * - 快照在途期间的本地写不丢——快照回包以「现值并集优先」合并；
 * - 登出清空 + epoch 使在途快照过期；
 * - 删会话时 Worker cleanup 连带删 kv 标记，切片残留项因行已消失不被消费。
 *
 * 形态选 Set 而非 Record：消费侧只做存在性判断（每帧 N 行各查一次），
 * 没有 archivedAt 的用途——归档页排序仍按会话本身的 compareConversations
 * （旧版归档页也按会话序，不按归档时刻）。
 */

import type { GlobalState, GlobalStore } from './global';
import type { AppWorkerHost } from './authState';
import type { ConversationKey } from '~/worker/contract';

/** null = 未加载（未登录/快照未回包）；空集 = 已加载但无归档 */
export type ArchiveSlice = ReadonlySet<string> | null;

export const ARCHIVE_INITIAL: ArchiveSlice = null;

/** 单条写入纯函数；无变化返回原引用（订阅不白跑） */
export function applyArchiveSet(
  current: ArchiveSlice,
  key: string,
  archived: boolean,
): ArchiveSlice {
  const keys = current ?? new Set<string>();
  if (keys.has(key) === archived) return current === null ? keys : current;
  const next = new Set(keys);
  if (archived) next.add(key);
  else next.delete(key);
  return next;
}

export interface ArchiveConnection {
  /** 归档/取消归档（会话行菜单调）。乐观改切片 + 回写 kv。 */
  setArchived(key: string, archived: boolean): void;
  /** Worker 崩溃重建后重拉快照（kv 是事实源，死区内的写已乐观在切片里，合并保住） */
  resync(): void;
  dispose(): void;
}

export function connectArchive(store: GlobalStore, host: AppWorkerHost): ArchiveConnection {
  let epoch = 0;
  let loadedUid: number | null = null;

  function load(): void {
    const seq = ++epoch;
    void host.call('chat/archived', null).then(
      (snap) => {
        if (seq !== epoch) return;
        store.set((prev) => {
          // 并集：快照在途期间的乐观归档不被旧快照覆盖（文件头边界 1）。
          // 取消归档在同一窗口内会被快照「复活」——窗口是登录决议的毫秒级，
          // 此时列表 UI 尚未存在，用户点不到菜单。
          const merged = new Set<string>(snap.keys);
          for (const k of prev.archive ?? []) merged.add(k);
          return { ...prev, archive: merged };
        });
      },
      () => undefined, // Worker 初始化失败：保持现值，崩溃重启后 resync 再拉
    );
  }

  function onAuth(auth: GlobalState['auth']): void {
    if (auth.status === 'logged-in') {
      if (auth.userId !== loadedUid) {
        loadedUid = auth.userId;
        load();
      }
    } else if (auth.status === 'logged-out') {
      loadedUid = null;
      epoch += 1;
      store.set({ archive: ARCHIVE_INITIAL });
    }
  }
  const offAuth = store.subscribe((s) => s.auth, onAuth);
  onAuth(store.get().auth);

  return {
    setArchived(key, archived) {
      store.set((prev) => {
        const next = applyArchiveSet(prev.archive, key, archived);
        return next === prev.archive ? prev : { ...prev, archive: next };
      });
      void host
        .call('chat/archive/set', {
          key: key as ConversationKey,
          archivedAt: archived ? Date.now() : null,
        })
        .catch(() => undefined); // 落库失败只损失一次标记（切片仍有，刷新即逝）
    },
    resync() {
      if (loadedUid !== null) load();
    },
    dispose() {
      offAuth();
    },
  };
}
